/**
 * Base class for a filter control adapter: manages ONE `.kpfsc__filter` control within a
 * {@link ShortcodeInstance}, abstracting how a control type reads/writes/observes its value so the instance's
 * reload logic stays type-agnostic. The default targets a single native control (`<select>` or input);
 * checkbox/radio/button-group types extend it. The adapter is chosen per filter from the `data-filter-type`
 * attribute the template emits (absent = select), so the theme decides control type + placement.
 */
export default class FilterControl {
    /** @member {jQuery} The `.kpfsc__filter` wrapper element. */
    #$el;

    /** @member {ShortcodeInstance} The owning shortcode instance. */
    #instance;

    /**
     * @param {jQuery} $filter The `.kpfsc__filter` wrapper.
     * @param {ShortcodeInstance} instance
     */
    constructor($filter, instance) {
        this.#$el = $filter;
        this.#instance = instance;
    }

    /**
     * @returns {jQuery} The `.kpfsc__filter` wrapper (used e.g. to test `.kpfsc--hidden`).
     */
    get $el() {
        return this.#$el;
    }

    /**
     * @returns {ShortcodeInstance}
     */
    get instance() {
        return this.#instance;
    }

    /**
     * The filter key, taken from the first `[name]` control (trailing `[]` stripped). This is the key used
     * in the `filterSelected` map and sent to the server.
     *
     * @returns {string}
     */
    get name() {
        const raw = this.control().attr("name") || "";
        return raw.replace(/\[\]$/, "");
    }

    /**
     * Human-readable name for this filter, used as the applied-filter chip prefix ("Category: News"). Prefers
     * the `data-filter-label` the template emits (the source's singular label), falling back to the visible
     * title span, then the machine name.
     *
     * @returns {string}
     */
    get label() {
        return (this.#$elLabel()) || this.name;
    }

    /** @returns {string} data-filter-label attr, else the .kpfsc__filter--text span text, else "". */
    #$elLabel() {
        const attr = (this.$el.attr("data-filter-label") || "").trim();
        if(attr) {
            return attr;
        }
        return (this.$el.find(".kpfsc__filter--text").first().text() || "").trim();
    }

    /**
     * The primary control element within the filter wrapper. Multi-input types (checkbox groups) override
     * the value methods below rather than relying on a single control.
     *
     * @returns {jQuery}
     */
    control() {
        return this.#$el.find("[name]").first();
    }

    /**
     * Type-specific setup (e.g. initializing a widget). No-op by default.
     */
    init() {}

    /**
     * Applies batch filter-data for this control: `{ options?: {value=>label}, counts?: {value=>count} }`.
     * No-op by default; control types that show option lists / labels (select, checkbox/radio/button groups)
     * override this to re-seed options and/or render "(N)" counts + disable zero-count options.
     *
     * @param {object} data
     */
    applyFilterData(data) {}

    /**
     * Reads the current value (string, or array for multi-value controls).
     *
     * @returns {*}
     */
    getValue() {
        return this.control().val();
    }

    /**
     * Writes the value WITHOUT firing a change event (used by clear + history restore).
     *
     * @param {*} value
     */
    setValue(value) {
        this.control().val(value);
    }

    /**
     * Clears the control to its empty state.
     */
    reset() {
        this.setValue(null);
    }

    /**
     * The active selections as `[{ value, label }]` for the applied-filter chips — one entry per selected
     * option. Base implementation reads the native `<select>` (select2 keeps it in sync); the empty
     * placeholder option is skipped. GroupFilter overrides for input groups.
     *
     * @returns {{value: string, label: string}[]}
     */
    getSelectionLabels() {
        const out = [];
        this.control().find("option:selected").each((i, el) => {
            if(el.value === "") {
                return; // the "All" placeholder isn't an active selection
            }
            out.push({ value: el.value, label: (el.textContent || "").trim() });
        });
        return out;
    }

    /**
     * Removes a single value from the current selection and fires change (drives the standard refetch). For
     * multi-value controls only that value is dropped; single-value controls clear. Polymorphic via
     * getValue/setValue, so it works for selects and input groups alike.
     *
     * @param {string} value
     */
    removeValue(value) {
        const current = this.getValue();
        const next = Array.isArray(current)
            ? current.filter((v) => String(v) !== String(value))
            : "";
        this.setValue(next);
        this.fireChange();
    }

    /* ---- Hash URL-sync slug translation (server supplies the maps in settings.filterSlugs) ---- */

    /** @returns {object|null} This filter's `{ urlSlug, values }` entry from settings.filterSlugs, if any. */
    #slugInfo() {
        return (this.instance.store.get("filterSlugs") || {})[this.name] || null;
    }

    /** @returns {string} The URL-hash key slug for this filter (server-resolved; falls back to the name). */
    get urlSlug() {
        const info = this.#slugInfo();
        return (info && info.urlSlug) || this.name;
    }

    /**
     * Maps selected value(s) → slug(s) for the hash. Values with no map entry pass through unchanged (sources
     * whose values are already their own slug: post-type/date/meta). Empty values are dropped.
     *
     * @param {*} values
     * @returns {string[]}
     */
    slugsFor(values) {
        const info = this.#slugInfo();
        const map = (info && info.values) || {};
        const arr = Array.isArray(values) ? values : (values === "" || values == null ? [] : [values]);

        return arr
            .filter((v) => v !== "" && v != null)
            .map((v) => (map[String(v)] !== undefined ? map[String(v)] : String(v)));
    }

    /**
     * Maps hash slug(s) → value(s) for restore. When this filter has a slug map (taxonomy/author), unknown
     * slugs are IGNORED (untrusted URL input). Without a map (value is its own slug), the slug is the value.
     *
     * @param {string[]|string} slugs
     * @returns {Array}
     */
    valuesForSlugs(slugs) {
        const info = this.#slugInfo();
        const map = (info && info.values) || {};
        const hasMap = Object.keys(map).length > 0;

        const reverse = {};
        for(const [value, slug] of Object.entries(map)) {
            reverse[String(slug)] = value;
        }

        const arr = Array.isArray(slugs) ? slugs : [slugs];
        return arr
            .map((s) => {
                const key = String(s);
                if(reverse[key] !== undefined) return reverse[key];
                return hasMap ? null : key; // unknown slug under a map → drop; no map → slug IS the value
            })
            .filter((v) => v !== null && v !== "");
    }

    /** @returns {string[]} The current selection expressed as hash slugs (for buildHash). */
    getSelectionSlugs() {
        return this.slugsFor(this.getValue());
    }

    /**
     * Sets this control's value from hash slug(s) (used on hash restore). No change event is fired here — the
     * caller manages the reload.
     *
     * @param {string[]|string} slugs
     */
    setValueBySlugs(slugs) {
        this.setValue(this.valuesForSlugs(slugs));
    }

    /**
     * Binds the control's native change event to the given handler (the instance's reload logic).
     *
     * @param {Function} handler
     */
    bindChange(handler) {
        this.control().on("change", handler);
    }

    /**
     * Programmatically triggers the control's change event so bound handlers run (used by clear + history
     * restore, where the instance's lock decides whether the reload actually proceeds).
     */
    fireChange() {
        this.control().trigger("change");
    }
}
